import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { IAudioGeneration, AudioGenerationStatus, IUserModel } from '@packages/global';
import StatusBadge from './StatusBadge';

interface AudioGenerationCardProps {
  item: IAudioGeneration;
  models: IUserModel[];
  onEdit: (item: IAudioGeneration) => void;
  onDelete: (id: string) => void;
  onUpdate: (updatedItem: IAudioGeneration) => void;
  onAudioPlay: (audioElement: HTMLAudioElement) => void;
}

export default function AudioGenerationCard({ item, models, onEdit, onDelete, onAudioPlay }: AudioGenerationCardProps) {
  const { t, i18n } = useTranslation(['generation', 'common']);
  const audioRef = useRef<HTMLAudioElement>(null);

  // 🔥 直接使用 props 中的 item，不再维护本地状态
  // WebSocket 状态更新由页面级别处理，通过 props 传递下来

  const getModelName = (modelId: string) => {
    const model = models.find(m => m.id === modelId);
    return model ? model.title : modelId;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString(i18n.language);
  };

  const handleDownload = async () => {
    if (!item.resultAudioUrl) return;
    try {
      const response = await fetch(item.resultAudioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audio-generation-${item.id}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('下载失败:', error);
    }
  };

  // 监听音频播放事件
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      onAudioPlay(audio);
    };

    audio.addEventListener('play', handlePlay);
    return () => {
      audio.removeEventListener('play', handlePlay);
    };
  }, [onAudioPlay]);

  return (
    <div className="w-60 bg-bg-surface border border-border-subtle rounded-2xl p-4 hover:shadow-[0_0_20px_rgba(255,92,0,0.15)] hover:border-primary hover:-translate-y-1 transition-all duration-300">
      <div className="flex flex-col h-full min-h-[200px]">
        {/* 主要内容 */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-50 truncate text-sm">
              {getModelName(item.modelId)}
            </h4>
            <p className="text-xs text-gray-500">
              {formatDate(item.createdAt)}
            </p>
          </div>
          <StatusBadge status={item.status} />
        </div>

        <div className="text-sm text-gray-200 mb-2 line-clamp-3">
          {item.prompt}
        </div>

        {/* 错误信息 */}
        {item.status === AudioGenerationStatus.FAILED && item.resultMessage && (
          <div className="text-sm text-red-400 mb-2 line-clamp-1" title={item.resultMessage}>
            {item.resultMessage}
          </div>
        )}

        {/* 占位符，将操作按钮推到底部 */}
        <div className="flex-1"></div>

        {/* 音频播放器 */}
        {item.resultAudioUrl && (
          <audio
            ref={audioRef}
            controls
            className="w-full h-8 mb-3 dark:[filter:invert(1)_hue-rotate(180deg)]"
          >
            <source src={item.resultAudioUrl} type="audio/mpeg" />
            {t('audio.browserNotSupported')}
          </audio>
        )}

        {/* 操作按钮 - 始终在底部 */}
        <div className="flex justify-end gap-2">
          {item.resultAudioUrl && (
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all"
              title={t('audio.card.downloadAudio')}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 rounded-lg bg-bg-elevated hover:bg-border-default text-gray-300 hover:text-white transition-all"
            title={t('audio.card.createFromThis')}
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-lg bg-bg-elevated hover:bg-red-500 text-gray-300 hover:text-white transition-all"
            title={t('common:delete')}
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}